// src/components/UpdateVideoAdmin.jsx
import React, { useEffect, useState, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { Container, Button } from 'react-bootstrap'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import axios from 'axios'
import { getAccessToken } from '../services/AuthService'
import { UserContext } from '../context'
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import { toast } from 'react-toastify'

import CreateCategory from './CreateCategory'
import FullscreenDraftEditor from './FullscreenDraftEditor'
import FontFamilyControl from './FontFamilyControl'
import { EDITOR_TOOLBAR, STYLE_MAP } from './editorToolbar'
import './CreateVideoAdmin.css' 

/* ------------------------ Category dropdown ------------------------ */
const CategoryList = ({ getCategory, field, form, ...props }) => {
  const [categories, setCategories] = useState([])

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = getAccessToken()
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/category_list/`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
        )
        setCategories(data || [])
      } catch (err) {
        toast.error(`Error obteniendo categorías: ${err.message}`)
      }
    }
    fetchCategories()
  }, [getCategory])

  return (
    <select
      {...field}
      {...props}
      id="admin-video-category"
      className={`va__select ${props.className || ''}`}
      onChange={(e) => form.setFieldValue(field.name, e.target.value)}
    >
      {categories.map((c) => (
        <option key={c.id} value={c.title} data-key={c.id}>
          {c.title}
        </option>
      ))}
    </select>
  )
}

/* ---------------------------- Main ---------------------------- */
const UpdateVideoAdmin = ({ isLoggedIn }) => {
  const { video_id } = useParams()
  const navigate = useNavigate()
  const [state] = useContext(UserContext)

  const [loading, setLoading] = useState(true)
  const [isSubmitted, setSubmitted] = useState(false)

  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [refreshCategories, setRefreshCategories] = useState(false)

  // Editor (fuente/color) — una sola fuente de verdad, como en CreateVideoAdmin
  const [descState, setDescState] = useState(() => EditorState.createEmpty())
  const [activeFont, setActiveFont] = useState(null)
  const [descError, setDescError] = useState('')
  const [fsOpen, setFsOpen] = useState(false)

  // Datos del video
  const [initialValues, setInitialValues] = useState({
    title: '',
    image: '',
    url: '',
    free: false,
    categories: '',
  })
  const [imagePreview, setImagePreview] = useState(null)
  const [existingImageUrl, setExistingImageUrl] = useState(null)

  // Cargar video
  useEffect(() => {
    const getVideo = async () => {
      try {
        const token = getAccessToken()
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/video_detail/${video_id}/`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
        )

        // description
        try {
          const raw = JSON.parse(data.description)
          setDescState(EditorState.createWithContent(convertFromRaw(raw)))
        } catch {
          setDescState(EditorState.createEmpty())
        }

        // imagen (para preview si existe)
        if (data.image) {
          setExistingImageUrl(data.image)
          setImagePreview(data.image)
        }

        setInitialValues({
          title: data.title || '',
          image: '',
          url: data.url || '',
          free: !!data.free,
          categories: (data.categories && data.categories[0]?.title) || '',
        })
      } catch (err) {
        console.error(err)
        toast.error('No se pudo cargar el video.')
      } finally {
        setLoading(false)
      }
    }
    getVideo()
  }, [video_id])

  // Mantener fuente activa al teclear
  const handleDescChange = (next) => {
    if (activeFont && !next.getCurrentInlineStyle().has(activeFont)) {
      const merged = next.getCurrentInlineStyle().add(activeFont)
      next = EditorState.setInlineStyleOverride(next, merged)
    }
    setDescState(next)
  }

  const vimeoEmbedPattern = /player\.vimeo\.com\/video\/\d+/i

  // Validación (igual que CreateVideoAdmin: descripción va fuera de Formik)
  const validate = (values) => {
    const errors = {}
    if (!descState.getCurrentContent().hasText()) {
      errors.__desc = 'x'
      setDescError('La descripción es requerida')
    } else {
      setDescError('')
    }
    if (!values.title) errors.title = 'El título es requerido'
    if (!values.url || !vimeoEmbedPattern.test(values.url)) {
      errors.url = 'Pega el código de inserción (embed) de Vimeo válido'
    }
    if (!values.categories) errors.categories = 'Selecciona una categoría'
    return errors
  }

  const handleSubmit = async (values) => {
    // guard de descripción
    if (!descState.getCurrentContent().hasText()) {
      setDescError('La descripción es requerida')
      return
    }

    try {
      const token = getAccessToken()

      // categorías (mantiene tu contrato: option attributes)
      const select = document.getElementById('admin-video-category')
      const selectedOption = select?.options[select.selectedIndex]
      const optionAttributes = Array.from(selectedOption?.attributes || []).reduce((acc, a) => {
        acc[a.name] = a.value
        return acc
      }, {})

      const fd = new FormData()
      // enviamos sólo lo editable (PATCH)
      if (values.title) fd.append('title', values.title)
      fd.append('categories', JSON.stringify(optionAttributes))
      if (values.url) fd.append('url', values.url)
      fd.append('free', values.free)

      // image solo si el usuario sube una nueva
      if (values.image && typeof values.image === 'object') {
        fd.append('image', values.image)
      }

      // descripción
      const content = JSON.stringify(convertToRaw(descState.getCurrentContent()))
      fd.append('description', content)

      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/api/video_detail/${video_id}/`,
        fd,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
      )

      toast.success('Video actualizado con éxito 🎉')
      setSubmitted(true)
    } catch (err) {
      console.error(err)
      toast.error(`Error actualizando video: ${err.message}`)
    }
  }

  const handleDelete = async () => {
    try {
      const token = getAccessToken()
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/video_detail/${video_id}/`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
      )
      toast.success('Video eliminado con éxito')
      navigate('/videos')
    } catch (err) {
      console.error(err)
      toast.error(`Error eliminando video: ${err.message}`)
    }
  }

  if (!isLoggedIn || (state.user && !state.user.is_staff)) return <Navigate to="/log-in" />
  if (isSubmitted) return <Navigate to="/videos" />
  if (loading) return <Container className="video-admin"><p>Cargando…</p></Container>

  return (
    <Container className="video-admin">
      <div className="va__header">
        <h1>Editar video</h1>
        <p>Actualiza la información del video, portada y contenido.</p>
      </div>

      <Formik initialValues={initialValues} enableReinitialize validate={validate} onSubmit={handleSubmit}>
        {({ isSubmitting, setFieldValue, values }) => (
          <>
            <Form className="va__grid">
              {/* LEFT */}
              <div className="va__col">
                {/* Básicos */}
                <section className="va__card">
                  <h2 className="va__card-title">Básicos</h2>

                  <div className="va__field">
                    <label htmlFor="title">Título del video</label>
                    <Field id="title" name="title" type="text" placeholder="Ej. Vinyasa Flow – Energía Matutina" />
                    <ErrorMessage name="title" component="div" className="va__error" />
                  </div>

                  <div className="va__field">
                    <label htmlFor="image">Portada (imagen)</label>
                    <div className="va__uploader">
                      <input
                        id="image"
                        name="image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0]
                          setFieldValue('image', file || '')
                          const url = file ? URL.createObjectURL(file) : existingImageUrl
                          setImagePreview((prev) => {
                            if (prev && prev !== existingImageUrl) URL.revokeObjectURL(prev)
                            return url || null
                          })
                        }}
                      />
                      <div className="va__uploader-help">JPG o PNG. Recomendado 1280×720 o superior.</div>
                    </div>
                    <ErrorMessage name="image" component="div" className="va__error" />
                  </div>

                  <div className="va__field va__switch">
                    <label className="form-switch">
                      <Field type="checkbox" name="free" />
                      <span className="slider" aria-hidden="true"></span>
                      <span className="switch-label">Marcar como video gratuito</span>
                    </label>
                  </div>
                </section>

                {/* Descripción */}
                <section className="va__card">
                  <div className="va__card-title-row">
                    <h2 className="va__card-title">Descripción</h2>
                    <div className="va__editor-controls">
                      <FontFamilyControl
                        editorState={descState}
                        setEditorState={setDescState}
                        setActiveFont={setActiveFont}
                      />
                      <button type="button" className="btn btn--ghost" onClick={() => setFsOpen(true)}>
                        Pantalla completa
                      </button>
                    </div>
                  </div>

                  {!fsOpen && (
                    <>
                      <Editor
                        editorState={descState}
                        onEditorStateChange={handleDescChange}
                        wrapperClassName="va__editor-wrapper"
                        editorClassName="va__editor"
                        toolbarClassName="va__editor-toolbar"
                        toolbar={EDITOR_TOOLBAR}
                        customStyleMap={STYLE_MAP}
                        editorStyle={{ color: '#111' }}
                      />
                      {descError && <div className="va__error">{descError}</div>}
                      <div className="va__hint">Edita la descripción del video.</div>
                    </>
                  )}
                </section>

                {/* Video URL */}
                <section className="va__card">
                  <h2 className="va__card-title">Video (Vimeo)</h2>
                  <div className="va__field">
                    <label htmlFor="url">Pega el código de inserción (embed) de Vimeo</label>
                    <Field as="textarea" id="url" name="url" rows="5" placeholder={`<iframe src="https://player.vimeo.com/video/123456789" ...></iframe>`} />
                    <ErrorMessage name="url" component="div" className="va__error" />
                    <div className="va__helper">
                      Asegúrate de que <strong>src</strong> contenga <code>https://player.vimeo.com/video/&lt;ID&gt;</code>.
                    </div>
                  </div>
                </section>

                {/* Categoría */}
                <section className="va__card">
                  <div className="va__card-title-row">
                    <h2 className="va__card-title">Categoría</h2>
                    <button type="button" className="btn btn--ghost" onClick={() => setShowCategoryModal(true)}>
                      + Nueva categoría
                    </button>
                  </div>

                  <div className="va__field">
                    <label htmlFor="admin-video-category">Selecciona una categoría</label>
                    <Field
                      as="select"
                      name="categories"
                      component={CategoryList}
                      className="va__select"
                      getCategory={refreshCategories}
                    />
                    <ErrorMessage name="categories" component="div" className="va__error" />
                  </div>
                </section>

                {/* Acciones */}
                <div className="va__actions">
                  <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Guardando…' : 'Actualizar video'}
                  </button>
                  <Button variant="danger" className="ms-2" onClick={handleDelete}>
                    Eliminar
                  </Button>
                </div>
              </div>

              {/* RIGHT: preview */}
              <aside className="va__preview">
                <div className="va__preview-card">
                  <div className="va__preview-media">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Vista previa" />
                    ) : (
                      <div className="va__preview-placeholder">Vista previa de portada</div>
                    )}
                    {values.free && <span className="va__badge">Gratis</span>}
                  </div>
                  <div className="va__preview-body">
                    <h3>{values.title || 'Título del video'}</h3>
                    <div className="va__preview-meta">{values.categories || 'Sin categoría'}</div>
                    <div className="va__preview-note">La vista previa se actualiza a medida que editas.</div>
                  </div>
                </div>
              </aside>
            </Form>

            {/* Fullscreen Editor */}
            {fsOpen && (
              <FullscreenDraftEditor
                editorState={descState}
                onChange={handleDescChange}
                onClose={() => setFsOpen(false)}
                setActiveFont={setActiveFont}
              />
            )}

            {/* Modal categorías */}
            {showCategoryModal && (
              <CreateCategory
                onClose={() => setShowCategoryModal(false)}
                setGetCategories={setRefreshCategories}
              />
            )}
          </>
        )}
      </Formik>
    </Container>
  )
}

export default UpdateVideoAdmin
